/**
 * Attribution capture — phase 3 of the attribution plan.
 * https://claude.ai/code/artifact/3a84fe3c-ccd0-4ffb-a935-2b9593465b4a
 *
 * One script, one call: on every page it sends page_view (plus product_view
 * on product pages, batched into the same request) to the first-party
 * collector on t.duavaa.com. The collector sets the two-year visitor cookie
 * as an HTTP header — the whole reason it exists — validates, stamps and
 * forwards to the planner's log.
 *
 * DUAL-RUN: this runs ALONGSIDE the existing n8n webhook scripts, which
 * stay untouched until a week of parallel counts agree (the plan's phase-6
 * gate). Nothing here replaces anything yet.
 *
 * FAIL SILENT, ALWAYS: attribution must never cost a shopper anything.
 * Every step is wrapped; a blocked or failed call is a thinner report,
 * never an error a customer sees. Until the owner deploys the worker on
 * t.duavaa.com this whole script is a no-op that fails quietly.
 *
 * CONSENT: the real Shopify.customerPrivacy signal is read per event and
 * sent with it. The worker enforces the EU/UK gate server-side; sending
 * the raw signal (rather than pre-filtering here) means every stored row
 * carries its own lawful basis.
 */
(function () {
  'use strict';

  if (window.__duavaaAttribLoaded) return; // double-include guard
  window.__duavaaAttribLoaded = true;

  var COLLECT_URL = 'https://t.duavaa.com/collect';

  function uuid() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) { /* fall through */ }
    // RFC-4122-shaped fallback for old WebViews; the worker re-mints an
    // invalid id anyway, so this only needs to be unique enough.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /** Market + locale + currency from the theme's own source of truth. */
  function marketInfo() {
    try {
      var m = window.MarketConfig && window.MarketConfig.detectMarket();
      if (!m || !m.config) return {};
      return {
        // The default entry stores 'INR' in its market field; the log wants
        // the market NAME, consistent with the strings the worker gates on.
        market: m.key === 'default' ? 'India' : m.config.market,
        locale: m.config.locale || m.key,
        currency: m.config.currency
      };
    } catch (e) {
      return {};
    }
  }

  /** The real consent signal, or null when the API is not present. */
  function consentState() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return null;
      return {
        analytics: cp.analyticsProcessingAllowed ? !!cp.analyticsProcessingAllowed() : undefined,
        marketing: cp.marketingAllowed ? !!cp.marketingAllowed() : undefined
      };
    } catch (e) {
      return null;
    }
  }

  function queryParams() {
    var out = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      sp.forEach(function (v, k) {
        if (!(k in out)) out[k] = v; // first-wins, matching the planner
      });
    } catch (e) { /* empty object is fine */ }
    return out;
  }

  /** Product id on product pages: analytics meta first, JSON script second. */
  function productGid() {
    try {
      if (window.location.pathname.indexOf('/products/') === -1) return null;
      var meta = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.meta;
      var id = meta && meta.product && (meta.product.id || meta.product.product_id);
      if (!id) {
        var el = document.querySelector('script[data-product-json]');
        if (el) id = (JSON.parse(el.textContent) || {}).id;
      }
      return id ? 'gid://shopify/Product/' + id : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * The one supported way for OTHER scripts to obtain the visitor id.
   *
   * The cookie is HttpOnly, so page JS cannot read it; the id only exists
   * client-side once a collector response has delivered it. Checkout pages
   * fire their request the moment they load, usually BEFORE that response
   * lands, so "read the global" silently yields nothing on exactly the page
   * that matters most. This resolver returns the cached id instantly when it
   * exists and otherwise asks the worker's GET /id directly, bounded by a
   * timeout: checkout must never be held hostage by attribution.
   */
  window.__duavaaGetVid = function (timeoutMs) {
    if (window.__duavaaVid) return Promise.resolve(window.__duavaaVid);
    return new Promise(function (resolve) {
      var done = false;
      var finish = function (v) { if (!done) { done = true; resolve(v || null); } };
      var timer = setTimeout(function () { finish(null); }, timeoutMs || 800);
      try {
        fetch(COLLECT_URL.replace('/collect', '/id'), { credentials: 'include' })
          .then(function (r) { return r.json(); })
          .then(function (d) {
            clearTimeout(timer);
            if (d && d.vid) window.__duavaaVid = d.vid;
            finish(d && d.vid);
          })
          .catch(function () { clearTimeout(timer); finish(null); });
      } catch (e) { clearTimeout(timer); finish(null); }
    });
  };

  function send() {
    try {
      var now = new Date().toISOString();
      var info = marketInfo();
      var consent = consentState();
      var base = {
        occurred_at: now,
        received_at: now, // stamped authoritatively by the worker
        url: String(window.location.href).slice(0, 2048),
        referrer: document.referrer ? String(document.referrer).slice(0, 2048) : null,
        query: queryParams(),
        market: info.market || null,
        locale: info.locale || null,
        currency: info.currency || null,
        consent: consent
      };

      var events = [Object.assign({ event_id: uuid(), kind: 'page_view' }, base)];
      var gid = productGid();
      if (gid) {
        events.push(Object.assign({ event_id: uuid(), kind: 'product_view', product_gid: gid }, base));
      }

      // credentials:'include' carries the duavaa_vid cookie to t.duavaa.com;
      // keepalive lets the request survive an immediate navigation.
      fetch(COLLECT_URL, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: events })
      }).then(function (r) {
        return r.json();
      }).then(function (data) {
        if (data && data.vid) {
          // The authoritative id, held for this page's lifetime. The Stripe
          // checkout payload reads it straight off window (buy-now-international).
          window.__duavaaVid = data.vid;
          // Native-checkout markets need it on the cart instead; no-ops when
          // the cart is empty.
          writeCartAttribute(data.vid);
        }
      }).catch(function () { /* silent by design */ });
    } catch (e) { /* silent by design */ }
  }

  /**
   * Write the visitor id onto the CART, for checkouts we do not control.
   *
   * India (and any native Shopify checkout) never touches the Stripe worker,
   * so the metadata path that carries the id on international orders does not
   * exist there. Shopify copies cart attributes onto the order as note
   * attributes by itself, which makes this the same join with no moving parts.
   *
   * ONLY WHEN A CART ALREADY EXISTS. /cart/update.js on an empty cart CREATES
   * one, and a cart minted on every pageview would show up as a flood of
   * abandoned carts in reports the business actually reads. theme.liquid
   * publishes the server-rendered item count for exactly this gate.
   *
   * Once per session per id: the attribute survives on the cart, so rewriting
   * it on every page would be a POST per pageview for no gain.
   */
  function writeCartAttribute(vid) {
    try {
      if (!vid) return;
      if (!(Number(window.__duavaaCartCount) > 0)) return;
      var stamp = 'duavaa_cart_vid';
      try {
        if (sessionStorage.getItem(stamp) === vid) return;
      } catch (e) { /* private mode: fall through and just write it */ }

      var body = 'attributes[_duavaa_vid]=' + encodeURIComponent(vid);
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body,
        keepalive: true
      }).then(function () {
        try { sessionStorage.setItem(stamp, vid); } catch (e) { /* ignore */ }
      }).catch(function () { /* silent by design */ });
    } catch (e) { /* silent by design */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', send, { once: true });
  } else {
    send();
  }
})();
